import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Check } from "lucide-react";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { OWNER_PHOTO, PHONE_DISPLAY, PHONE_TEL, waLink, onCall, onWhatsApp } from "@/lib/site";

const line = {
  hidden: { opacity: 0, y: "110%" },
  show: (i) => ({
    opacity: 1,
    y: "0%",
    transition: { duration: 0.85, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const TITLE = ["Guincho 24 Horas", "em Jundiaí", "e Região"];
const CHECKS = ["Atendimento 24 horas", "Jundiaí e região", "Carros e motos", "Transporte seguro"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.04, 1.12]);

  return (
    <section id="inicio" ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden grain">
      <motion.div style={{ y, scale }} className="absolute inset-0 lg:inset-y-0 lg:left-auto lg:right-0 lg:w-[48%] z-0">
        <img
          src={OWNER_PHOTO}
          alt="Proprietário da DG Guinchos em frente ao caminhão guincho 24 horas em Jundiaí"
          className="h-full w-full object-cover object-[50%_18%]"
          fetchpriority="high"
        />
      </motion.div>
      {/* Mobile darken for text legibility */}
      <div className="lg:hidden absolute inset-0 z-[2] bg-gradient-to-t from-black via-black/70 to-black/35" />
      {/* Desktop: blend the photo's left edge into the black text area */}
      <div className="hidden lg:block absolute inset-y-0 right-0 w-[48%] z-[2] bg-gradient-to-r from-black via-black/25 to-transparent" />

      <div className="container-x relative z-10 pb-28 md:pb-32 pt-32 lg:pr-[46%]">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="inline-flex items-center gap-2 mb-6 border border-brand/60 bg-brand/10 px-4 py-2 rounded-sm"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand" />
          </span>
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.22em] text-white">
            Atendimento 24 horas · 7 dias por semana
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[1.06] tracking-tight text-white text-5xl sm:text-7xl lg:text-8xl max-w-5xl drop-shadow-[0_3px_24px_rgba(0,0,0,0.65)]">
          {TITLE.map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block pb-[0.1em] -mb-[0.1em]">
                {i === 1 ? (
                  <span className="text-brand">{t}</span>
                ) : (
                  t
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7 }}
          className="mt-6 max-w-xl text-base md:text-lg text-zinc-300 font-medium"
        >
          Atendimento rápido e seguro para carros, motos e transporte de veículos.
          Precisou de guincho? Fale agora com a DG Guinchos.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center"
        >
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            onClick={() => onWhatsApp("hero")}
            className="inline-flex items-center justify-center gap-2.5 bg-wapp hover:bg-wapp-hover text-black font-display font-bold uppercase tracking-wider text-lg px-7 py-4 rounded-sm transition-all active:scale-95 shadow-[0_0_30px_rgba(37,211,102,0.4)]"
          >
            <WhatsAppIcon className="h-6 w-6" /> Chamar no WhatsApp
          </a>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="hero-call-btn"
            onClick={() => onCall("hero")}
            className="inline-flex items-center justify-center gap-2.5 bg-transparent border-2 border-white/25 hover:border-brand hover:bg-brand text-white font-display font-bold uppercase tracking-wider text-lg px-7 py-4 rounded-sm transition-all active:scale-95"
          >
            <Phone className="h-5 w-5" /> Ligar Agora
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-8 flex flex-wrap gap-x-6 gap-y-2"
        >
          {CHECKS.map((c) => (
            <span key={c} className="flex items-center gap-2 text-sm font-semibold text-zinc-200">
              <Check className="h-4 w-4 text-brand" strokeWidth={3} /> {c}
            </span>
          ))}
        </motion.div>

        <motion.a
          href={`tel:${PHONE_TEL}`}
          onClick={() => onCall("hero-phone-line")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 0.6 }}
          className="mt-7 inline-flex items-center gap-2 text-2xl md:text-3xl font-display font-bold text-white"
        >
          <Phone className="h-6 w-6 text-brand" /> {PHONE_DISPLAY}
        </motion.a>
      </div>
    </section>
  );
};
